/**
 * Mock-«сервер» сторис: ведёт себя как будущие ручки из `contract.ts`, но живёт
 * в браузере. Временная замена API по просьбе заказчика — бэкенд пока не
 * сделан. Данные лежат в localStorage, поэтому у админки и у приложения
 * покупателя наборы РАЗНЫЕ (разные домены): созданное в админке витрина
 * увидит только с настоящим API.
 *
 * Проверки повторяют то, что должен проверять сервер, и отвечают тем же
 * `ApiError` с `validation_failed` / `not_found` — экраны не отличат mock от API.
 */
import { ApiError } from '../../errors'
import {
  STORY_BUTTON_MAX,
  STORY_DURATION_MAX,
  STORY_DURATION_MIN,
  STORY_MEDIA_MAX_BYTES,
  STORY_SLIDES_MAX,
  STORY_TITLE_MAX,
  type PublicStoryDto,
  type StoryDto,
  type StoryInput,
  type StoryMediaDto,
} from '../contract'
import { SEED_STORIES } from './seed'

/** Ровно то, что экраны ждут от источника сторис — mock или API. */
export interface StoriesBackend {
  list: () => Promise<StoryDto[]>
  get: (id: number) => Promise<StoryDto>
  create: (input: StoryInput) => Promise<StoryDto>
  update: (id: number, input: StoryInput) => Promise<StoryDto>
  remove: (id: number) => Promise<void>
  upload: (file: Blob) => Promise<StoryMediaDto>
  /** Витрина: `GET /stories`. */
  published: () => Promise<PublicStoryDto[]>
  /** Покупатель открыл сторис: `POST /stories/{id}/view` (сервер считает одного раз). */
  view: (id: number) => Promise<void>
}

export const MOCK_STORIES_KEY = 'dorixona.mock.stories.v1'

export interface MockStoreOptions {
  storage: Pick<Storage, 'getItem' | 'setItem'>
  /** Часы приложения (мс) — для периода показа и отметок времени. */
  now?: () => number
}

const invalid = (message: string) =>
  new ApiError({ code: 'validation_failed', status: 400, message })

/** Те же правила, что должен держать сервер (docs/notes/stories-contract.md). */
export function validateStory(input: StoryInput): void {
  if (!input.title_ru.trim()) throw invalid('Нужна подпись на русском')
  if (input.title_ru.length > STORY_TITLE_MAX || input.title_uz.length > STORY_TITLE_MAX) {
    throw invalid(`Подпись длиннее ${STORY_TITLE_MAX} символов`)
  }
  if (input.slides.length > STORY_SLIDES_MAX) {
    throw invalid(`Слайдов больше ${STORY_SLIDES_MAX}`)
  }
  if (input.is_active && input.slides.length === 0) {
    throw invalid('Без слайдов сторис не показать')
  }
  for (const slide of input.slides) {
    if (!slide.image_key) throw invalid('У слайда нет картинки')
    if (slide.duration_sec < STORY_DURATION_MIN || slide.duration_sec > STORY_DURATION_MAX) {
      throw invalid(`Длительность слайда — от ${STORY_DURATION_MIN} до ${STORY_DURATION_MAX} с`)
    }
    if (slide.button_ru.length > STORY_BUTTON_MAX || slide.button_uz.length > STORY_BUTTON_MAX) {
      throw invalid(`Подпись кнопки длиннее ${STORY_BUTTON_MAX} символов`)
    }
    const hasButton = Boolean(slide.button_ru.trim() || slide.button_uz.trim())
    if (hasButton && !/^(\/|https:\/\/)/.test(slide.link)) {
      throw invalid('Ссылка кнопки — путь приложения или адрес https://')
    }
  }
  if (input.starts_at && input.ends_at && input.ends_at <= input.starts_at) {
    throw invalid('Конец показа раньше начала')
  }
}

/** Показывается ли сторис покупателю прямо сейчас. */
export function isLive(story: StoryDto, now: Date): boolean {
  const at = now.toISOString()
  return (
    story.is_active &&
    story.slides.length > 0 &&
    (story.starts_at === null || story.starts_at <= at) &&
    (story.ends_at === null || story.ends_at > at)
  )
}

export function toPublic(story: StoryDto): PublicStoryDto {
  return {
    id: story.id,
    title_ru: story.title_ru,
    title_uz: story.title_uz,
    cover_url: story.cover_url,
    slides: story.slides.map(({ image_key: _key, ...slide }) => slide),
    updated_at: story.updated_at,
  }
}

/** Картинка как data-URL: в mock-хранилище ключ и адрес — одно и то же. */
export async function toDataUrl(file: Blob): Promise<string> {
  // В браузере (и в jsdom, где у File нет arrayBuffer) читает FileReader.
  if (typeof FileReader === 'function') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        resolve(reader.result as string)
      }
      reader.onerror = () => {
        reject(reader.error ?? new Error('Файл не прочитан'))
      }
      reader.readAsDataURL(file)
    })
  }
  const bytes = new Uint8Array(await file.arrayBuffer())
  let binary = ''
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  }
  return `data:${file.type || 'application/octet-stream'};base64,${btoa(binary)}`
}

export function createMockStoriesStore({
  storage,
  now = () => Date.now(),
}: MockStoreOptions): StoriesBackend {
  function read(): StoryDto[] {
    try {
      const raw = storage.getItem(MOCK_STORIES_KEY)
      if (raw === null) return structuredClone(SEED_STORIES)
      const parsed: unknown = JSON.parse(raw)
      return Array.isArray(parsed) ? (parsed as StoryDto[]) : structuredClone(SEED_STORIES)
    } catch {
      return structuredClone(SEED_STORIES)
    }
  }

  function write(stories: StoryDto[]): void {
    try {
      storage.setItem(MOCK_STORIES_KEY, JSON.stringify(stories))
    } catch {
      throw new ApiError({
        code: 'payload_too_large',
        status: 413,
        message: 'Хранилище браузера переполнено: удалите лишние слайды',
      })
    }
  }

  function find(stories: StoryDto[], id: number): StoryDto {
    const story = stories.find((s) => s.id === id)
    if (!story) throw new ApiError({ code: 'not_found', status: 404, message: 'Сторис не найдена' })
    return story
  }

  function build(input: StoryInput, base: StoryDto | null, id: number, nextSlideId: () => number) {
    const stamp = new Date(now()).toISOString()
    return {
      id,
      title_ru: input.title_ru.trim(),
      title_uz: input.title_uz.trim(),
      cover_key: input.cover_key,
      cover_url: input.cover_key,
      slides: input.slides.map((slide) => ({
        id: slide.id ?? nextSlideId(),
        image_key: slide.image_key,
        image_url: slide.image_key,
        duration_sec: slide.duration_sec,
        button_ru: slide.button_ru.trim(),
        button_uz: slide.button_uz.trim(),
        link: slide.link.trim(),
      })),
      starts_at: input.starts_at,
      ends_at: input.ends_at,
      sort: input.sort,
      is_active: input.is_active,
      views: base?.views ?? 0,
      created_at: base?.created_at ?? stamp,
      updated_at: stamp,
    } satisfies StoryDto
  }

  function slideCounter(stories: StoryDto[]) {
    let last = Math.max(0, ...stories.flatMap((s) => s.slides.map((slide) => slide.id)))
    return () => (last += 1)
  }

  const sorted = (stories: StoryDto[]) =>
    [...stories].sort((a, b) => a.sort - b.sort || a.id - b.id)

  /** Ответ «сервера» всегда асинхронный, а брошенная ошибка — отклонённый промис. */
  const reply = <T>(work: () => T) =>
    new Promise<T>((resolve) => {
      resolve(work())
    })

  return {
    list: () => reply(() => sorted(read())),

    get: (id) => reply(() => find(read(), id)),

    create: (input) =>
      reply(() => {
        validateStory(input)
        const stories = read()
        const id = Math.max(0, ...stories.map((s) => s.id)) + 1
        const story = build(input, null, id, slideCounter(stories))
        write([...stories, story])
        return story
      }),

    update: (id, input) =>
      reply(() => {
        validateStory(input)
        const stories = read()
        const story = build(input, find(stories, id), id, slideCounter(stories))
        write(stories.map((s) => (s.id === id ? story : s)))
        return story
      }),

    remove: (id) =>
      reply(() => {
        const stories = read()
        find(stories, id)
        write(stories.filter((s) => s.id !== id))
      }),

    // В браузере покупатель один: каждое открытие — просмотр.
    view: (id) =>
      reply(() => {
        const stories = read()
        find(stories, id)
        write(stories.map((s) => (s.id === id ? { ...s, views: (s.views ?? 0) + 1 } : s)))
      }),

    upload: async (file) => {
      if (file.size > STORY_MEDIA_MAX_BYTES) {
        throw new ApiError({ code: 'payload_too_large', status: 413, message: 'Файл больше 5 МБ' })
      }
      if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
        throw invalid('Нужна картинка JPEG, PNG или WebP')
      }
      const url = await toDataUrl(file)
      return { key: url, url }
    },

    published: () =>
      reply(() => {
        const at = new Date(now())
        return sorted(read())
          .filter((story) => isLive(story, at))
          .map(toPublic)
      }),
  }
}
